import { OpenImgContextProvider } from 'openimg/react'
import { useState } from 'react'
import {
	data,
	Link,
	Links,
	Meta,
	Outlet,
	Scripts,
	ScrollRestoration,
	useLoaderData,
	useMatches,
} from 'react-router'
import { HoneypotProvider } from 'remix-utils/honeypot/react'
import { type Route } from './+types/root.ts'
import appleTouchIconAssetUrl from './assets/favicons/apple-touch-icon.png'
import { GeneralErrorBoundary } from './components/error-boundary.tsx'
import Footer from './components/footer.tsx'
import { EpicProgress } from './components/progress-bar.tsx'
import { useToast } from './components/toaster.tsx'
import { EpicToaster } from './components/ui/sonner.tsx'
import {
	useOptionalTheme,
	useTheme,
} from './routes/resources+/theme-switch.tsx'
import fontsStyleSheetUrl from './styles/font.css?url'
import tailwindStyleSheetUrl from './styles/tailwind.css?url'
import { getUserId, logout } from './utils/auth.server.ts'
import { ClientHintCheck, getHints } from './utils/client-hints.tsx'
import { setCsrfToken } from './utils/csrf.sever.ts'
import { prisma } from './utils/db.server.ts'
import { getEnv } from './utils/env.server.ts'
import { useGoogleAnalytics } from './utils/gtag-hook.ts'
import { pipeHeaders } from './utils/headers.server.ts'
import { honeypot } from './utils/honeypot.server.ts'
import { combineHeaders, getDomainUrl, getImgSrc } from './utils/misc.tsx'
import { getNewsletter } from './utils/newsletter.server.ts'
import { useNonce } from './utils/nonce-provider.ts'
import { type Theme, getTheme } from './utils/theme.server.ts'
import { makeTimings, time } from './utils/timing.server.ts'
import { getToast } from './utils/toast.server.ts'

export const links: Route.LinksFunction = () => {
	return [
		// Preload svg sprite as a resource to avoid render blocking
		//{ rel: 'preload', href: iconsHref, as: 'image' },
		{
			rel: 'apple-touch-icon',
			sizes: '180x180',
			href: '/favicons/apple-touch-icon.png',
		},
		{
			rel: 'icon',
			type: 'image/png',
			sizes: '32x32',
			href: '/favicons/favicon-32x32.png',
		},
		{
			rel: 'icon',
			type: 'image/png',
			sizes: '16x16',
			href: '/favicons/favicon-16x16.png',
		},
		{ rel: 'icon', href: '/favicon.ico' },
		//{ rel: 'icon', type: 'image/svg+xml', href: faviconAssetUrl },
		{ rel: 'apple-touch-icon', href: appleTouchIconAssetUrl },
		{
			rel: 'manifest',
			href: '/site.webmanifest',
			crossOrigin: 'use-credentials',
		} as const, // necessary to make typescript happy
		{ rel: 'stylesheet', href: fontsStyleSheetUrl },
		{ rel: 'stylesheet', href: tailwindStyleSheetUrl },
		
	].filter(Boolean)
}

export const meta: Route.MetaFunction = ({ loaderData }) => {
	return [
		{ title: loaderData ? 'Jase owns the Internet' : 'JaseOwns.com | Error' },
		{ name: 'description', content: `JaseOwns.com is the online dumping ground of Jason Ramsey. He's a developer, gamer, husband and father who has been a nerd owning people on the internet since dial up. Includes online ramblings of an internet star. It's a far, far away star - it's kind of hard to see.` },
	]
}

export async function loader({ request }: Route.LoaderArgs) {
	const timings = makeTimings('root loader')
	const userId = await time(() => getUserId(request), {
		timings,
		type: 'getUserId',
		desc: 'getUserId in root',
	})

	// const user = userId
	// 	? await time(
	// 			() =>
	// 				prisma.user.findUnique({
	// 					select: {
	// 						id: true,
	// 						name: true,
	// 						username: true,
	// 						//image: { select: { objectKey: true } },
	// 						roles: {
	// 							select: {
	// 								name: true,
	// 								permissions: {
	// 									select: { entity: true, action: true, access: true },
	// 								},
	// 							},
	// 						},
	// 					},
	// 					where: { id: userId },
	// 				}),
	// 			{ timings, type: 'find user', desc: 'find user in root' },
	// 		)
	// 	: null
	// if (userId && !user) {
	// 	console.info('something weird happened')
	// 	// something weird happened... The user is authenticated but we can't find
	// 	// them in the database. Maybe they were deleted? Let's log them out.
	// 	await logout({ request, redirectTo: '/' })
	// }
	const { toast, headers: toastHeaders } = await getToast(request)
	const honeyProps = await honeypot.getInputProps()

	const hasSubscribed = await getNewsletter(request) === 'yes';
    const { csrfToken, headers: csrfTokenHeaders }  = await setCsrfToken(request);

	return data(
		{
			user: null,
			requestInfo: {
				hints: getHints(request),
				origin: getDomainUrl(request),
				path: new URL(request.url).pathname,
				userPrefs: {
					theme: getTheme(request),
					hasSubscribed,
				},
			},
			ENV: getEnv(),
			toast,
			honeyProps,
			csrfToken: csrfToken,
		},
		{
			headers: combineHeaders(
				{ 'Server-Timing': timings.toString() },
				toastHeaders,
				csrfTokenHeaders
			),
		},
	)
}

export const headers: Route.HeadersFunction = pipeHeaders


const Header = () => {
	const [navbar, setNavbar] = useState(false);

const liveButton = "";//<LiveButton />

return <header id="hero">
<div className="container max-w-6xl mx-auto px-6 py-12">
  <nav className="flex items-center justify-between font-bold text-white">
	<Link to="/">
				  <img
					  src={`/images/jase.gif`}
					  alt="Illustration of weird guy with wild hair, blinking his eyes"
				  />
		  </Link>
	<div className="hidden h-10 font-alata md:flex md:items-center md:space-x-8 text-black dark:text-white">
		<div className="group">
		  <Link to="/about">About</Link>
			<div className="mx-2 group-hover:border-b group-hover:border-blue-900 dark:group-hover:border-blue-50" />
	  </div>
	  <div className="group">
		  <Link to="/blog">Blog</Link>
			<div className="mx-2 group-hover:border-b group-hover:border-blue-900 dark:group-hover:border-blue-50" />
	  </div>
	  <div className="group">
		  <Link to="/dev">Projects</Link>
		  <div className="mx-2 group-hover:border-b group-hover:border-blue-900 dark:group-hover:border-blue-50" />
	  </div>
	  <div className="group">
		  <Link to="/gaming">Gaming</Link>
			<div className="mx-2 group-hover:border-b group-hover:border-blue-900 dark:group-hover:border-blue-50" />
	  </div>
	  <div className="group">
		  <Link to="/videos">Videos</Link>
			<div className="mx-2 group-hover:border-b group-hover:border-blue-900 dark:group-hover:border-blue-50" />
	  </div>
	  <div className="group">
		  <Link to="/schedule">Schedule</Link>
			<div className="mx-2 group-hover:border-b group-hover:border-blue-900 dark:group-hover:border-blue-50" />
	  </div>
	  <div className="group">
		  <Link to="/support">Support</Link>
		<div className="mx-2 group-hover:border-b group-hover:border-blue-900 dark:group-hover:border-blue-50" />
	  </div>
	  <div className="group">
		  <Link to="/discord">Discord</Link>
		<div className="mx-2 group-hover:border-b group-hover:border-blue-900 dark:group-hover:border-blue-50" />
	  </div>
	  <a href="https://shop.jaseowns.com" target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-white bg-blue-600 hover:bg-blue-700 border border-transparent focus:ring-4 focus:ring-blue-300 shadow-sm font-medium leading-5 rounded-lg text-sm px-4 py-2.5 focus:outline-none transition-colors">
		<svg className="w-4 h-4 me-1.5 -ms-0.5" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
		  <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 4h1.5L9 16m0 0h8m-8 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4Zm8 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4Zm-8.5-3h9.25L19 7H7.312"/>
		</svg>
		Merch
	  </a>
	  {liveButton}
	</div>
	<div className="md:hidden z-60">
	  <button
		onClick={() => setNavbar(!navbar)}
		id="mobile-menu-btn"
		type="button"
		className={`z-40 block hamburger md:hidden focus:outline-none ${navbar ? "open" : "" }`}
	  >
		<span className="hamburger-top"></span>
		<span className="hamburger-middle"></span>
		<span className="hamburger-bottom"></span>
	  </button>
	</div>
  </nav>

  <div
	id="mobile-menu"
	className={`z-50 absolute md:hidden top-0 bottom-0 left-0 flex-col self-end w-full min-h-screen py-1 pt-40 pl-12 space-y-3 text-lg text-white uppercase bg-black  ${navbar ? "flex" : "hidden"}`}
  >
		  <Link to="/about" onClick={() => setNavbar(false)}>About</Link>
		  <Link to="/blog" onClick={() => setNavbar(false)}>Blog</Link>
		  <Link to="/dev" onClick={() => setNavbar(false)}>Projects</Link>
		  <Link to="/gaming" onClick={() => setNavbar(false)}>Gaming</Link>
		  <Link to="/videos" onClick={() => setNavbar(false)}>Videos</Link>
		  <Link to="/schedule" onClick={() => setNavbar(false)}>Schedule</Link>
		  <Link to="/support" onClick={() => setNavbar(false)}>Support</Link>
		  <Link to="/discord" onClick={() => setNavbar(false)}>Discord</Link>
		  <a href="https://shop.jaseowns.com" target="_blank" rel="noopener noreferrer" onClick={() => setNavbar(false)} className="inline-flex items-center text-white bg-blue-600 hover:bg-blue-700 border border-transparent focus:ring-4 focus:ring-blue-300 shadow-sm font-medium leading-5 rounded-lg text-sm px-4 py-2.5 focus:outline-none transition-colors w-fit">
			<svg className="w-4 h-4 me-1.5 -ms-0.5" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
			  <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 4h1.5L9 16m0 0h8m-8 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4Zm8 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4Zm-8.5-3h9.25L19 7H7.312"/>
			</svg>
			Merch
		  </a>
		  {liveButton}
  </div>


</div>
</header>
}


function Document({
	children,
	nonce,
	theme = 'light',
	env = {},
}: {
	children: React.ReactNode
	nonce: string
	theme?: Theme
	env?: Record<string, string | undefined>
}) {
	const GA_MEASUREMENT_ID_CLIENT = "G-7DQ9DSXV54";
	useGoogleAnalytics();
	  
	const allowIndexing = ENV.ALLOW_INDEXING !== 'false'
	return (
		<html lang="en" className={`${theme} h-full overflow-x-hidden`}>
			<head>
				<ClientHintCheck nonce={nonce} />
				<Meta />
				<meta charSet="utf-8" />
				<meta name="viewport" content="width=device-width,initial-scale=1" />
				{allowIndexing ? null : (
					<meta name="robots" content="noindex, nofollow" />
				)}
				<Links />
				{GA_MEASUREMENT_ID_CLIENT && (
					<>
						<script
							async
							nonce={nonce}
							src={`https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID_CLIENT}`}
						/>
						<script
							nonce={nonce}
							dangerouslySetInnerHTML={{
								__html: `
								window.dataLayer = window.dataLayer || [];
								function gtag(){dataLayer.push(arguments);}
								gtag('js', new Date());
								gtag('config', '${GA_MEASUREMENT_ID_CLIENT}', {
									send_page_view: false
								});
								`,
						}}
						/>
					</>
				)}
			</head>
			{/* <body className="bg-background text-foreground"> */}
			<body className="flex h-full flex-col justify-between bg-day-300 text-white dark:bg-neutral-950">
				{/* <div id="debugger" style={{ position: 'fixed', padding: '10px', top: 0, left: 0, backgroundColor: 'white', color: 'black' }}>
					{theme}
				</div> */}
				{children}
				<script
					nonce={nonce}
					dangerouslySetInnerHTML={{
						__html: `window.ENV = ${JSON.stringify(env)}`,
					}}
				/>
				<ScrollRestoration nonce={nonce} />
				<Scripts nonce={nonce} />
			</body>
		</html>
	)
}

export function Layout({ children }: { children: React.ReactNode }) {
	// if there was an error running the loader, data could be missing
	const data = useLoaderData<typeof loader | null>()
	const nonce = useNonce()
	const theme = useOptionalTheme()

	return (
		<Document nonce={nonce} theme={theme} env={data?.ENV}>
			{children}
		</Document>
	)
}

/**
 * Routes can export `handle = { chromeless: true }` to drop the site header and
 * footer and render as a standalone page (e.g. the piñata joke pages).
 */
function useIsChromeless() {
	return useMatches().some(
		(match) => (match.handle as { chromeless?: boolean } | undefined)?.chromeless,
	)
}

function App() {
	const data = useLoaderData<typeof loader>()
	const theme = useTheme()
	const chromeless = useIsChromeless()
	useToast(data.toast)
	return (
		<OpenImgContextProvider
			optimizerEndpoint="/resources/images"
			getSrc={getImgSrc}
		>
			<div>
				{chromeless ? null : <Header />}
				{/* <header className="container py-6">
					<nav className="flex flex-wrap items-center justify-between gap-4 sm:flex-nowrap md:gap-8">
						<Logo />
						<div className="ml-auto hidden max-w-sm flex-1 sm:block">
							{searchBar}
						</div>
						<div className="flex items-center gap-10">
							{user ? (
								<UserDropdown />
							) : (
								<Button asChild variant="default" size="lg">
									<Link to="/login">Log In</Link>
								</Button>
							)}
						</div>
						<div className="block w-full sm:hidden">{searchBar}</div>
					</nav>
				</header> */}

				<div className="flex-1">
					<Outlet />
				</div>
			</div>
			{chromeless ? null : <Footer csrfToken={data.csrfToken?.toString() || 'no-csrf-token'} userPreference={data.requestInfo.userPrefs.theme} hasSubscribed={data.requestInfo.userPrefs.hasSubscribed} />}
			<EpicToaster closeButton position="top-center" theme={theme} />
			<EpicProgress />
		</OpenImgContextProvider>
	)
}

function AppWithProviders() {
	const data = useLoaderData<typeof loader>()
	return (
		<HoneypotProvider {...data.honeyProps}>
			<App />
		</HoneypotProvider>
	)
}

export default AppWithProviders

// this is a last resort error boundary. There's not much useful information we
// can offer at this level.
export const ErrorBoundary = GeneralErrorBoundary
